import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Flex, Pagination, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import Private from "../../../Components/Private"
import SelectComponent from "../../../Components/Select"
import TableComponent, { itemRender } from "../../../Components/Table"
import Icon from "../../../Icon"
import Images from "../../../Images"
import { getDates } from "../../../Utils/utils"
import HealthMobilePagination from "../components/HealthMobilePagination"
import HealthSubpageHeader from "../components/HealthSubpageHeader"
import HealthSubpageLayout from "../components/HealthSubpageLayout"
import {
  getColumnsByReasonCode,
  ReasonCodeDescription
} from "../components/Table/byReasonCodeColumns"
import { formatAmount, formatCount } from "../components/Table/columns"
import { useHealthFilters, useHealthSubpage, useTableSettings } from "../hooks"
import Creators from "../reducer"
import { cardBrandToTextMap, getCardOptionsForReasonCodes } from "../utils/cardOptions"
import { useBusinessAccountId } from "../VAMP/hooks/useBusinessAccountId"

const { Text } = Typography

const pageSize = 50

const BreakdownByReasonCodePage = ({
  filters,
  selectCards,
  dataByReasonCodeFetching,
  dataByReasonCode,
  theme,
  ...props
}) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const businessId = useBusinessAccountId()
  const { results, other } = dataByReasonCode
  const { dataSource } = useSelector((state) => state.health)

  const count = dataByReasonCode?.count ?? 0

  const [pageNumber, setPageNumber] = useState(1)
  const [mobileColumn, setMobileColumn] = useState("type")
  const { ordering, handleChangeTable } = useHealthSubpage(pageSize)

  const { handleChangeFilter } = useHealthFilters(props.changeFilters)

  const helpRef = useRef({ firstLoading: true })

  const cardsFilter = useMemo(() => {
    if (!selectCards || selectCards.length === 0 || selectCards === "all") {
      return null
    }
    return selectCards?.join(",")
  }, [selectCards])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      props.getDataByReasonCode({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        business_account: businessId,
        card_brand: cardsFilter,
        source: dataSource ?? "stripe",
        ...period_query
      })

      if (helpRef.current.firstLoading) {
        helpRef.current.firstLoading = false
      }
    }
  }, [
    filters?.period,
    businessId,
    ordering,
    pageNumber,
    pageSize,
    props.getDataByReasonCode,
    cardsFilter,
    dataSource
  ])

  const [otherExpanded, setOtherExpanded] = useState(false)
  const [expandedOtherKeys, setExpandedOtherKeys] = useState([])

  const columns = useMemo(
    () =>
      getColumnsByReasonCode({
        t,
        withSort: true,
        theme,
        expandedOtherKeys,
        otherExpanded
      }),
    [t, theme, expandedOtherKeys, otherExpanded]
  )

  const cardsOptions = useMemo(() => getCardOptionsForReasonCodes(theme), [theme])
  useTableSettings(count, pageNumber, pageSize)

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Type"), value: "type" },
      { label: t("Chargebacks"), value: "chargebacks" },
      {
        label: t("Chargebacks without us"),
        value: "chargebacks_without_us"
      },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((option) => option.value === mobileColumn)?.label || t("Column")

  const handleChangeFilterCards = useCallback((newFilters) => {
    props.changeCounties(newFilters?.["card_brand"])
    setPageNumber(1)
  }, [])

  const otherData = useMemo(() => {
    for (const value of Object.values(other ?? {})) {
      if (value) return other
    }
    return null
  }, [other])

  const dataSourceTable = useMemo(() => {
    const mainRows = results ?? []
    const isLastPage = pageNumber * pageSize >= count

    if (!isLastPage || !otherData) return mainRows

    const otherRow = { ...otherData, reason_code: "other", isOtherRow: true }
    const otherInnerRows = []

    if (otherExpanded) {
      for (const otherCardTypeRow of otherData?.card_types ?? []) {
        const otherCardTypeKey = `other-${otherCardTypeRow.card_type}`
        otherInnerRows.push({
          ...otherCardTypeRow,
          reason_code: otherCardTypeKey,
          isOtherCardTypeRow: true
        })
        if (expandedOtherKeys.includes(otherCardTypeKey)) {
          otherInnerRows.push(
            ...(otherCardTypeRow?.dispute_types?.map((reasonCodeRow) => ({
              ...reasonCodeRow,
              reason_code: `other-${otherCardTypeRow.card_type}-${reasonCodeRow.dispute_type}`,
              isOtherReasonCodeTypeRow: true
            })) ?? [])
          )
        }
      }
    }

    return [...mainRows, otherRow, ...otherInnerRows]
  }, [results, otherData, otherExpanded, expandedOtherKeys, pageNumber, pageSize, count])

  const otherRowClickHandler = () => {
    setOtherExpanded((prev) => !prev)
  }

  const otherCardTypeRowHandler = (record) => {
    const key = record.reason_code
    setExpandedOtherKeys((prevKeys) =>
      prevKeys.includes(key) ? prevKeys.filter((k) => k !== key) : [...prevKeys, key]
    )
  }

  const renderMobileReasonLabel = (record) => {
    let image = Images?.[record?.card_brand?.toLowerCase()] || null
    const isOtherRow = record?.isOtherRow
    const isOtherCardTypeRow = record?.isOtherCardTypeRow
    const isOtherReasonCodeTypeRow = record?.isOtherReasonCodeTypeRow
    const isExpandable = (isOtherRow && record?.card_types?.length > 0) || isOtherCardTypeRow
    const isExpanded =
      expandedOtherKeys?.includes(record?.reason_code) || (isOtherRow && otherExpanded)

    let text = record?.reason_code
    if (isOtherRow) text = "Other"
    if (isOtherCardTypeRow)
      text = cardBrandToTextMap[record?.card_type?.toLowerCase()] || record?.card_type
    if (isOtherReasonCodeTypeRow) text = ""
    if (isOtherCardTypeRow) {
      image = Images?.[record?.card_type?.toLowerCase()] || null
    }

    let marginLeft = 0
    if (isOtherCardTypeRow) marginLeft = 16
    if (isOtherReasonCodeTypeRow) marginLeft = 48

    return (
      <Flex
        gap={8}
        align="flex-start"
        className="by-reason-code-subpage-mobile__left"
        style={{ marginLeft }}
      >
        {image && <img src={image} alt={text} width={20} height={20} />}
        <Flex gap={2} vertical className="by-reason-code-subpage-mobile__reason-wrap">
          <Text strong>{text}</Text>
          <ReasonCodeDescription description={record?.tooltip || []} />
        </Flex>
        {isExpandable && (
          <Icon
            type="chevron_down_small"
            className={cls("chevron_down", { expanded: isExpanded })}
          />
        )}
      </Flex>
    )
  }

  const renderMobileValueCell = (record) => {
    switch (mobileColumn) {
      case "type": {
        let text = record?.type
        if (record?.isOtherReasonCodeTypeRow) {
          text = record?.dispute_type?.[0]?.toUpperCase() + record?.dispute_type?.slice(1)
        }
        if (record?.isOtherRow) text = "Other"
        return <Text strong>{text}</Text>
      }
      case "chargebacks":
        return (
          <Flex gap={2} vertical align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks_without_us":
        return (
          <Flex gap={2} vertical align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count_without_us)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount_without_us)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical align="flex-end">
            <Text strong>{formatCount(record?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{record?.prevention_rate}%</Text>
      default:
        return null
    }
  }

  const mobileRowClickHandler = (record) => {
    const isOtherRow = record.reason_code === "other"
    const isOtherCardTypeRow = record.isOtherCardTypeRow
    if (isOtherRow) {
      otherRowClickHandler()
    } else if (isOtherCardTypeRow) {
      otherCardTypeRowHandler(record)
    }
  }

  return (
    <HealthSubpageLayout backText={t("Back to Health")} isMobile={isMobile}>
      {isMobile ? (
        <Flex vertical gap={12} className="by-reason-code-subpage-mobile__header-block">
          <HealthSubpageHeader title={t("Breakdown by Reason Code")} />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox by-reason-code-subpage-mobile__dropdown"
            id="card_brand"
            setDropdownFilters={handleChangeFilterCards}
            dropdownFilters={selectCards}
            allBtnText="All"
            label="Card"
            data={cardsOptions}
            newMarkStyle={true}
            sortDisabled
          />
          <SelectComponent
            size="large"
            value={mobileColumn}
            onChange={setMobileColumn}
            options={mobileColumnOptions}
            style={{ width: "100%" }}
          />
        </Flex>
      ) : (
        <HealthSubpageHeader title={t("Breakdown by Reason Code")}>
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox"
            id="card_brand"
            setDropdownFilters={handleChangeFilterCards}
            dropdownFilters={selectCards}
            allBtnText="All"
            label="Card"
            data={cardsOptions}
            newMarkStyle={true}
            sortDisabled
          />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </HealthSubpageHeader>
      )}
      <div>
        {isMobile ? (
          <Flex vertical gap={12} className="by-reason-code-subpage-mobile">
            <div className="by-reason-code-subpage-mobile__header">
              <Text type="secondary">{t("Reason code")}</Text>
              <Text type="secondary" className="by-reason-code-subpage-mobile__header-right">
                {selectedMobileColumnLabel}
              </Text>
            </div>

            {dataSourceTable.map((record, index) => {
              const isOtherRow = record.reason_code === "other"
              const isOtherCardTypeRow = record.isOtherCardTypeRow
              const isExpandable =
                (isOtherRow && record?.card_types?.length > 0) || isOtherCardTypeRow

              return (
                <div
                  key={record.reason_code}
                  className={cls("by-reason-code-subpage-mobile__row", {
                    "by-reason-code-subpage-mobile__row--last": index === dataSource.length - 1,
                    "by-reason-code-subpage-mobile__row--expandable": isExpandable
                  })}
                  onClick={() => mobileRowClickHandler(record)}
                >
                  {renderMobileReasonLabel(record)}
                  <div className="by-reason-code-subpage-mobile__right">
                    {renderMobileValueCell(record)}
                  </div>
                </div>
              )
            })}
          </Flex>
        ) : (
          <TableComponent
            loading={dataByReasonCodeFetching}
            dataSource={dataSourceTable}
            columns={columns}
            scroll={{ x: "auto" }}
            onChange={handleChangeTable}
            rowKey="reason_code"
            className="health-breakdown-by-reason-code-table"
            withOutPagination
            onRow={(record) => {
              const isOtherRow = record.reason_code === "other"
              const isOtherCardTypeRow = record.isOtherCardTypeRow
              const isOtherReasonCodeTypeRow = record.isOtherReasonCodeTypeRow
              const isExpandable =
                (isOtherRow && record?.card_types?.length > 0) || isOtherCardTypeRow
              let onClickHandler = null
              if (isOtherRow) {
                onClickHandler = otherRowClickHandler
              } else if (isOtherCardTypeRow) {
                onClickHandler = otherCardTypeRowHandler
              }
              return {
                onClick: () => onClickHandler?.(record),
                className: cls({
                  "expandable-row": isExpandable,
                  divider: record?.isDivider,
                  "other-row": isOtherRow,
                  "other-card-type-row": isOtherCardTypeRow,
                  "other-reason-code-type-row": isOtherReasonCodeTypeRow
                })
              }
            }}
          />
        )}
        {isMobile ? (
          <HealthMobilePagination
            current={pageNumber}
            pageSize={pageSize}
            total={count || 0}
            onChange={setPageNumber}
          />
        ) : (
          <Pagination
            rootClassName={"ant-table-pagination by-reason-code-pagination"}
            onChange={(page) => setPageNumber(page)}
            total={count || 0}
            pageSize={pageSize}
            current={pageNumber}
            showLessItems={true}
            showTotal={(total, range) => (
              <Text>
                Showing <Text strong>{`${range[0]}-${range[1]} of ${total}`}</Text> results
              </Text>
            )}
            itemRender={(_, type, originalElement) =>
              pageSize >= count ? null : itemRender(_, type, originalElement, t)
            }
            showSizeChanger={false}
            showQuickJumper={false}
          />
        )}
      </div>
    </HealthSubpageLayout>
  )
}

const mapStateToProps = (state) => ({
  filters: state.health.filters,
  dataByReasonCodeFetching: state.health.dataByReasonCodeFetching,
  dataByReasonCode: state.health.dataByReasonCode,
  selectCards: state.health.selectCards,
  theme: state.nav.theme
})

const mapDispatchToProps = (dispatch) => ({
  getDataByReasonCode: (params) => dispatch(Creators.getDataByReasonCodeRequest(params)),
  changeFilters: (filters) => dispatch(Creators.healthSetProp("filters", filters)),
  changeCounties: (counties) => dispatch(Creators.healthSetProp("selectCards", counties))
})

export default connect(mapStateToProps, mapDispatchToProps)(BreakdownByReasonCodePage)
